import * as Phaser from 'phaser';
import type { Primary } from '../beam/tracer';
import { PALETTE, switchLitColor } from '../render/palette';

// All sizing is expressed as a ratio of cellSize so the same entity scales
// cleanly between the puzzle (cell=64) and onboarding (cell=48) contexts.
const OUTER_HALO_R = 0.406;
const INNER_HALO_R = 0.281;
const RIM_R = 0.219;
const RECESS_R = 0.172;
const RIM_STROKE = 1.2;
const SINGLE_DOT_R = 0.053;
const CLUSTER_DOT_R = 0.038;
const CLUSTER_RADIUS = 0.07;

export class SwitchEntity extends Phaser.GameObjects.Container {
  public lit = false;
  public readonly id: string;
  public readonly requires: ReadonlyArray<Primary>;
  private readonly outerHalo: Phaser.GameObjects.Arc;
  private readonly innerHalo: Phaser.GameObjects.Arc;
  private readonly rim: Phaser.GameObjects.Arc;
  private readonly recess: Phaser.GameObjects.Arc;
  private readonly sheen: Phaser.GameObjects.Arc;
  private readonly accentRing: Phaser.GameObjects.Arc;
  private readonly dots: Phaser.GameObjects.Arc[] = [];

  constructor(
    scene: Phaser.Scene,
    cellSize: number,
    gridX: number,
    gridY: number,
    id: string,
    requires: ReadonlyArray<Primary> = [],
  ) {
    super(scene, (gridX + 0.5) * cellSize, (gridY + 0.5) * cellSize);
    this.id = id;
    this.requires = requires;
    const litFill = switchLitColor(requires);

    // Halos start hidden via master alpha so the dark off-state isn't tinted
    // by their litFill colour. setLit(true) brings them up.
    this.outerHalo = scene.add
      .circle(0, 0, cellSize * OUTER_HALO_R, litFill)
      .setOrigin(0.5)
      .setAlpha(0)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.innerHalo = scene.add
      .circle(0, 0, cellSize * INNER_HALO_R, litFill)
      .setOrigin(0.5)
      .setAlpha(0)
      .setBlendMode(Phaser.BlendModes.ADD);

    // Rim — dark coin with a thin stroke that brightens when lit.
    this.rim = scene.add
      .circle(0, 0, cellSize * RIM_R, PALETTE.SWITCH_RECESS_DARKER)
      .setOrigin(0.5)
      .setStrokeStyle(RIM_STROKE, PALETTE.SWITCH_RIM_DIM, 1);

    // Recess centre — fills with the required color when lit.
    this.recess = scene.add.circle(0, 0, cellSize * RECESS_R, PALETTE.SWITCH_RECESS).setOrigin(0.5);

    // Sheen — soft white wash on top of the lit fill so it reads as glass.
    this.sheen = scene.add
      .circle(0, 0, cellSize * RECESS_R, 0xffffff)
      .setOrigin(0.5)
      .setAlpha(0);

    // Outer accent ring — only visible when lit.
    this.accentRing = scene.add
      .circle(0, 0, cellSize * RIM_R)
      .setOrigin(0.5)
      .setFillStyle(0xffffff, 0)
      .setStrokeStyle(1, litFill, 1)
      .setAlpha(0);

    this.add([this.outerHalo, this.innerHalo, this.rim, this.recess, this.sheen, this.accentRing]);

    // Requirement dots.
    if (requires.length === 1) {
      const dot = scene.add
        .circle(0, 0, cellSize * SINGLE_DOT_R, primaryColor(requires[0]!))
        .setOrigin(0.5)
        .setAlpha(0.7);
      this.dots.push(dot);
      this.add(dot);
    } else if (requires.length >= 2) {
      const r = cellSize * CLUSTER_RADIUS;
      const step = (Math.PI * 2) / requires.length;
      const offset = -Math.PI / 2;
      for (let i = 0; i < requires.length; i++) {
        const a = offset + i * step;
        const dot = scene.add
          .circle(
            Math.cos(a) * r,
            Math.sin(a) * r,
            cellSize * CLUSTER_DOT_R,
            primaryColor(requires[i]!),
          )
          .setOrigin(0.5)
          .setAlpha(0.7);
        this.dots.push(dot);
        this.add(dot);
      }
    }

    scene.add.existing(this);
  }

  setLit(lit: boolean): void {
    if (this.lit === lit) return;
    this.lit = lit;
    const litFill = switchLitColor(this.requires);

    if (lit) {
      this.outerHalo.setFillStyle(litFill).setAlpha(0.5);
      this.innerHalo.setFillStyle(litFill).setAlpha(0.7);
      this.rim.setStrokeStyle(RIM_STROKE, litFill, 1);
      this.recess.setFillStyle(litFill, 0.85);
      this.sheen.setAlpha(0.18);
      this.accentRing.setStrokeStyle(1, litFill, 0.9).setAlpha(1);
      for (const d of this.dots) d.setAlpha(0.95);
    } else {
      this.outerHalo.setAlpha(0);
      this.innerHalo.setAlpha(0);
      this.rim.setStrokeStyle(RIM_STROKE, PALETTE.SWITCH_RIM_DIM, 1);
      this.recess.setFillStyle(PALETTE.SWITCH_RECESS, 1);
      this.sheen.setAlpha(0);
      this.accentRing.setAlpha(0);
      for (const d of this.dots) d.setAlpha(0.7);
    }
  }
}

function primaryColor(c: Primary): number {
  return c === 'R' ? PALETTE.R : c === 'G' ? PALETTE.G : PALETTE.B;
}
